import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

const DeviceContext = createContext(void 0);
const DeviceProvider = ({ children }) => {
  const [device, setDevice] = useState("desktop");
  const [safeArea, setSafeArea] = useState(0);
  const [isIosOld, setIsIosOld] = useState(false);
  const [isScroll, setScroll] = useState(false);
  const [isPopupScroll, setPopupScroll] = useState(false);
  const [isLargeText, setIsLargeText] = useState(() => {
    return localStorage.getItem("isLargeText") === "true";
  });
  const [themeMode, setThemeModeState] = useState(() => {
    return localStorage.getItem("themeMode") || "light";
  });
  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
  }, []);
  const toggleLargeText = useCallback((isLargeTextParam) => {
    setIsLargeText(
      (prev) => typeof isLargeTextParam === "boolean" ? isLargeTextParam : !prev
    );
  }, []);
  useEffect(() => {
    localStorage.setItem("isLargeText", String(isLargeText));
    localStorage.setItem("themeMode", themeMode);
  }, [isLargeText, themeMode]);
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDevice("ios");
      const osMatch = navigator.userAgent.match(/OS (\d+)_/i);
      const versionMatch = navigator.userAgent.match(/Version\/(\d+)/i);
      const appMatch = navigator.userAgent.match(/;(?:IPhone|iPad|iPod);(\d+)\./);
      const iosVersion = Math.max(
        osMatch ? parseInt(osMatch[1], 10) : 0,
        versionMatch ? parseInt(versionMatch[1], 10) : 0,
        appMatch ? parseInt(appMatch[1], 10) : 0
      );
      console.log("iOs VersionL:", iosVersion);
      if (iosVersion > 0 && iosVersion < 18) {
        setIsIosOld(true);
      }
    } else if (/android/.test(userAgent)) {
      setDevice("android");
    } else {
      setDevice("desktop");
    }
  }, []);
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("ios", "aos", "desktop", "ios_old");
    if (device === "ios") {
      html.classList.add("ios");
      if (isIosOld) html.classList.add("ios_old");
    } else if (device === "android") {
      html.classList.add("aos");
    } else {
      html.classList.add("desktop");
    }
    const frameId = requestAnimationFrame(() => {
      setSafeArea(
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--safe-area-inset-top").trim() || "0"
        )
      );
    });
    return () => {
      html.classList.remove("ios", "aos", "desktop", "ios_old");
      cancelAnimationFrame(frameId);
    };
  }, [device, isIosOld]);
  useEffect(() => {
    const html = document.documentElement;
    html.dataset.largeText = String(isLargeText && device !== "desktop");
  }, [isLargeText, device]);
  useEffect(() => {
    const system = window.matchMedia("(prefers-color-scheme:dark)");
    const apply = () => {
      document.body.classList.toggle(
        "dark",
        isLargeText && themeMode === "dark" || themeMode === "system" && system.matches
      );
    };
    apply();
    if (isLargeText && themeMode === "system") {
      system.addEventListener("change", apply);
      return () => system.removeEventListener("change", apply);
    }
  }, [isLargeText, themeMode]);
  const deviceValue = useMemo(
    () => ({
      device,
      isIos: device === "ios",
      isIosOld,
      isAndroid: device === "android",
      isDesktop: device === "desktop",
      isScroll,
      setScroll,
      isPopupScroll,
      setPopupScroll,
      isLargeText,
      toggleLargeText,
      themeMode,
      setThemeMode,
      safeAreaTop: safeArea
    }),
    [
      device,
      safeArea,
      isScroll,
      isPopupScroll,
      isLargeText,
      themeMode,
      toggleLargeText,
      setThemeMode,
      isIosOld
    ]
  );
  useEffect(() => {
    const htmlEl = document.documentElement;
    if (isScroll || isPopupScroll) {
      htmlEl.setAttribute("data-custom-keyboard", "true");
    } else {
      htmlEl.removeAttribute("data-custom-keyboard");
    }
    return () => {
      htmlEl.removeAttribute("data-custom-keyboard");
    };
  }, [isPopupScroll, isScroll]);
  return /* @__PURE__ */ jsx(DeviceContext.Provider, { value: deviceValue, children });
};
const useDevice = () => {
  const context = useContext(DeviceContext);
  if (context === void 0) {
    throw new Error("useDevice must be used within DeviceProvider");
  }
  return context;
};

export { DeviceProvider, useDevice };
